import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Clock, Users, Video } from "lucide-react";
import { format } from "date-fns";

interface Session {
  id: string;
  title: string;
  description: string | null;
  session_type: string;
  duration_minutes: number;
  price: number;
  max_attendees: number;
  start_time: string;
  end_time: string;
  meeting_link: string | null;
  status: string;
  host_id: string;
  profiles?: { username: string };
}

interface Booking {
  id: string;
  session_id: string;
  payment_status: string;
  payment_amount: number;
  attended: boolean;
  live_sessions: Session;
}

const DashboardSessions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [myBookings, setMyBookings] = useState<Booking[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Load available sessions
      const { data: sessionsData } = await supabase
        .from("live_sessions")
        .select("*")
        .eq("status", "scheduled")
        .gte("start_time", new Date().toISOString())
        .order("start_time", { ascending: true });

      // Fetch host profiles separately
      if (sessionsData && sessionsData.length > 0) {
        const hostIds = [...new Set(sessionsData.map(s => s.host_id))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, username")
          .in("id", hostIds);

        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        const enrichedSessions = sessionsData.map(s => ({
          ...s,
          profiles: profilesMap.get(s.host_id),
        }));

        setSessions(enrichedSessions as any);
      } else {
        setSessions([]);
      }

      // Load my bookings
      const { data: bookingsData } = await supabase
        .from("session_bookings")
        .select("*, live_sessions(*)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      setMyBookings(bookingsData as any || []);
    } catch (error: any) {
      toast.error("Failed to load sessions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSessionTypeBadge = (type: string) => {
    const variants: Record<string, any> = {
      "1-on-1": "default",
      "group": "secondary",
      "workshop": "outline",
    };
    return <Badge variant={variants[type] || "secondary"}>{type}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold">Live Sessions</h1>
        </div>

        {/* My Bookings */}
        {myBookings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>My Upcoming Sessions</CardTitle>
              <CardDescription>Sessions you've booked</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {myBookings.map((booking) => (
                  <Card key={booking.id}>
                    <CardHeader>
                      <CardTitle className="text-lg">{booking.live_sessions.title}</CardTitle>
                      <CardDescription>
                        {booking.live_sessions.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(booking.live_sessions.start_time), "PPP")}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4" />
                        {format(new Date(booking.live_sessions.start_time), "p")} -{" "}
                        {format(new Date(booking.live_sessions.end_time), "p")}
                      </div>
                      {booking.live_sessions.meeting_link && (
                        <Button
                          className="w-full mt-4"
                          onClick={() => window.open(booking.live_sessions.meeting_link!, "_blank")}
                        >
                          <Video className="w-4 h-4 mr-2" />
                          Join Session
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Sessions */}
        <Card>
          <CardHeader>
            <CardTitle>Available Sessions</CardTitle>
            <CardDescription>Book a live session with our instructors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sessions.length === 0 ? (
                <p className="col-span-full text-center text-muted-foreground py-8">
                  No sessions available at the moment
                </p>
              ) : (
                sessions.map((session) => (
                  <Card key={session.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{session.title}</CardTitle>
                        {getSessionTypeBadge(session.session_type)}
                      </div>
                      <CardDescription>{session.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(session.start_time), "PPP")}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {format(new Date(session.start_time), "p")} ({session.duration_minutes}min)
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Max {session.max_attendees} attendees
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t">
                        <span className="text-2xl font-bold">
                          {session.price === 0 ? "Free" : `$${(session.price / 100).toFixed(2)}`}
                        </span>
                        <Button 
                          onClick={() => navigate(`/dashboard/sessions/${session.id}`)}
                        >
                          View & Book
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardSessions;
